import { useEffect, useMemo } from 'react';
import { IntlShape } from 'react-intl';
import { RouteSuggestion } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/types';
import { memberDetailMessages } from './messages';
import { MemberSection } from './types';
import {
  getMemberDetailSectionPath,
  getVisibleMemberDetailSections,
} from './sections';

type UseMemberDetailOmnibarSuggestionsParams = {
  fountainLifeUXEnabled: boolean;
  fountainLifePowerUserEnabled: boolean;
  regenerativeMedicineOpsEnabled: boolean;
  genericCompositeReportEnabled: boolean;
  memberJourneyProjectionEnabled?: boolean;
  memberId?: string;
  memberDisplayName: string;
  section?: MemberSection;
  formatMessage: IntlShape['formatMessage'];
  searchValue: string;
  registerSuggestions: (options: {
    id: string;
    loading: boolean;
    suggestions: RouteSuggestion[];
    handler?: () => void;
  }) => void;
  unregisterSuggestions: (id: string) => void;
  setShowZoriChat: (value: boolean | ((prev: boolean) => boolean)) => void;
};

export const useMemberDetailOmnibarSuggestions = ({
  fountainLifeUXEnabled,
  fountainLifePowerUserEnabled,
  regenerativeMedicineOpsEnabled,
  genericCompositeReportEnabled,
  memberJourneyProjectionEnabled = false,
  memberId,
  memberDisplayName,
  section,
  formatMessage,
  searchValue,
  registerSuggestions,
  unregisterSuggestions,
  setShowZoriChat,
}: UseMemberDetailOmnibarSuggestionsParams) => {
  const memberRouteSuggestions: RouteSuggestion[] = useMemo(() => {
    if (!fountainLifeUXEnabled || !memberId) {
      return [];
    }
    const sections = getVisibleMemberDetailSections({
      fountainLifePowerUserEnabled,
      regenerativeMedicineOpsEnabled,
      genericCompositeReportEnabled,
      memberJourneyProjectionEnabled,
    }).map(sectionConfig => ({
      label: formatMessage(memberDetailMessages[sectionConfig.labelMessage]),
      slug: sectionConfig.section,
    }));

    return sections.map(({ label, slug }) => ({
      value: `${memberDisplayName} ${label}`.toLowerCase(),
      display: `${memberDisplayName} > ${label}`,
      path: getMemberDetailSectionPath(memberId, slug),
      type: 'route' as const,
    }));
  }, [
    fountainLifePowerUserEnabled,
    regenerativeMedicineOpsEnabled,
    fountainLifeUXEnabled,
    genericCompositeReportEnabled,
    memberJourneyProjectionEnabled,
    formatMessage,
    memberDisplayName,
    memberId,
  ]);

  useEffect(() => {
    if (!fountainLifeUXEnabled) {
      unregisterSuggestions('memberDetailRouteSuggestions');
      return;
    }
    const filteredRoutes = memberRouteSuggestions.filter(route =>
      route.value.includes(searchValue.toLowerCase()),
    );
    registerSuggestions({
      id: 'memberDetailRouteSuggestions',
      loading: false,
      suggestions: filteredRoutes,
    });
    return () => {
      unregisterSuggestions('memberDetailRouteSuggestions');
    };
  }, [
    fountainLifeUXEnabled,
    memberRouteSuggestions,
    registerSuggestions,
    searchValue,
    unregisterSuggestions,
  ]);

  useEffect(() => {
    if (!fountainLifeUXEnabled || !memberId) {
      unregisterSuggestions('memberDetailZoriChat');
      return;
    }

    const query = searchValue.toLowerCase();
    const zoriSuggestion: RouteSuggestion = {
      value: 'zori chat',
      display: `${memberDisplayName} > Zori Chat`,
      path:
        !section || section === 'landing'
          ? getMemberDetailSectionPath(memberId, 'landing')
          : getMemberDetailSectionPath(memberId, section),
      type: 'route',
    };
    const suggestions = zoriSuggestion.value.includes(query)
      ? [zoriSuggestion]
      : [];

    registerSuggestions({
      id: 'memberDetailZoriChat',
      loading: false,
      suggestions,
      handler: () => {
        setShowZoriChat(true);
      },
    });

    return () => {
      unregisterSuggestions('memberDetailZoriChat');
    };
  }, [
    fountainLifeUXEnabled,
    memberDisplayName,
    memberId,
    registerSuggestions,
    searchValue,
    section,
    setShowZoriChat,
    unregisterSuggestions,
  ]);
};
